import React, { useState, useEffect, useMemo } from 'react';
import { firestore } from './common/firebase';
import { collection, doc, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from './common/Auth';
import { useCurrentSystem } from './context/SystemContext';
import {
	getSceneType,
	normalizeSceneLooks,
	normalizeCycleHold,
	MIN_CYCLE_HOLD_SECONDS,
	CYCLE_HOLD_STEP_SECONDS,
} from './sceneUtils';
import SVGColorBlock from './SVGColorBlock';
import ExpertColorPanel from './components/ExpertColorPanel';
import SceneColorPicker from './components/SceneColorPicker';
import { rotateColors, shuffleColorsToZones } from './lookColorUtils';
import { defaultZoneName } from './zoneUtils';
import { ChevronDown, ChevronRight, RotateCcw, Shuffle } from 'lucide-react';
import { canUseAiHelp } from './aiHelp/entitlement';
import { isAiHelpConfigured } from './aiHelp/api';
import AskJulesPanel, { JulesIcon } from './aiHelp/AskJulesPanel';

function SceneEditor({ scene, open, doneEdit, numColors = 4, colorNames = [] }) {
	const currentUser = useAuth();
	const { currentSystem } = useCurrentSystem();
	const [values, setValues] = useState({ name: '', type: 'single', looks: [] });
	const [expandedLooks, setExpandedLooks] = useState({});
	const [expertLookIndex, setExpertLookIndex] = useState(0);
	const [colorHelper, setColorHelper] = useState('expert'); // 'jules' | 'expert'

	const julesEnabled = canUseAiHelp(currentUser)
		&& currentSystem?.aiHelpEnabled === true
		&& isAiHelpConfigured();

	useEffect(() => {
		if (julesEnabled) setColorHelper('jules');
		else setColorHelper('expert');
	}, [julesEnabled, open]);

	const makeDefaultLook = () => ({
		colors: Array.from({ length: numColors || 4 }, () => ({ r: 128, g: 128, b: 128 })),
		hold: MIN_CYCLE_HOLD_SECONDS,
	});

	useEffect(() => {
		if (scene) {
			const looks = normalizeSceneLooks(scene, numColors);
			const type = getSceneType(scene);
			setValues({ name: scene.name || '', type, looks });
			setExpandedLooks(type === 'single' ? { 0: true } : {});
		} else {
			setValues({
				name: 'New Scene',
				type: 'single',
				looks: [makeDefaultLook()],
			});
			setExpandedLooks({ 0: true });
		}
	}, [scene, numColors]);

	const scenePalette = useMemo(() => {
		const seen = new Set();
		const out = [];
		for (const look of values.looks || []) {
			for (const c of look.colors || []) {
				const key = `${c.r},${c.g},${c.b}`;
				if (!seen.has(key)) {
					seen.add(key);
					out.push(c);
				}
			}
		}
		return out;
	}, [values.looks]);

	const setType = (type) => {
		if (type === 'single') {
			const firstLook = values.looks?.[0] || makeDefaultLook();
			setValues({ ...values, type, looks: [{ ...firstLook, hold: 0 }] });
			setExpandedLooks({ 0: true });
		} else {
			const looks = (values.looks?.length ? values.looks : [makeDefaultLook()]).map((l) => ({
				...l,
				hold: normalizeCycleHold(l.hold),
			}));
			setValues({ ...values, type, looks });
			setExpandedLooks({});
		}
	};

	const setLookColor = (lookIndex, colorIndex, rgb) => {
		const currentLooks = [...(values.looks || [])];
		const look = { ...currentLooks[lookIndex] };
		const colors = [...(look.colors || [])];
		colors[colorIndex] = { r: rgb.r, g: rgb.g, b: rgb.b };
		look.colors = colors;
		currentLooks[lookIndex] = look;
		setValues({ ...values, looks: currentLooks });
	};

	const toggleLookExpanded = (index) => {
		setExpandedLooks((prev) => ({ ...prev, [index]: !prev[index] }));
		setExpertLookIndex(index);
	};

	const padColors = (colors) => {
		const defaults = makeDefaultLook().colors;
		const out = (colors || []).map((c) => ({ ...c }));
		while (out.length < (numColors || 4)) {
			out.push({ ...(defaults[out.length] || { r: 128, g: 128, b: 128 }) });
		}
		return out.slice(0, numColors || 4);
	};

	const applyExpertLook = (lookIndex, colors) => {
		const newLooks = [...(values.looks || [])];
		newLooks[lookIndex] = {
			...newLooks[lookIndex],
			colors: padColors(colors),
		};
		setValues({ ...values, looks: newLooks });
	};

	const applyExpertNewLook = (colors) => {
		const currentLooks = values.looks || [];
		const newIndex = currentLooks.length;
		setValues({
			...values,
			type: 'cycle',
			looks: [
				...currentLooks,
				{ colors: padColors(colors), hold: MIN_CYCLE_HOLD_SECONDS },
			],
		});
		setExpandedLooks((prev) => ({ ...prev, [newIndex]: true }));
		setExpertLookIndex(newIndex);
	};

	const applyExpertCycle = (looks) => {
		setValues({
			...values,
			type: 'cycle',
			looks: looks.map((l) => ({
				colors: padColors(l.colors),
				hold: normalizeCycleHold(l.hold),
			})),
		});
		setExpandedLooks({});
	};

	const applyExpertAllLooks = (colors) => {
		const padded = padColors(colors);
		setValues({
			...values,
			looks: (values.looks || []).map((l) => ({ ...l, colors: padded.map((c) => ({ ...c })) })),
		});
	};

	const applyJulesResult = (payload) => {
		const namePatch = payload?.name ? { name: payload.name } : {};
		if (payload?.type === 'cycle' && payload.looks?.length) {
			setValues((prev) => ({
				...prev,
				...namePatch,
				type: 'cycle',
				looks: payload.looks.map((l) => ({
					colors: padColors(l.colors),
					hold: normalizeCycleHold(l.hold),
				})),
			}));
			setExpandedLooks({});
			return;
		}
		if (payload?.colors?.length) {
			const lookIndex = expertLookIndex || 0;
			setValues((prev) => {
				const newLooks = [...(prev.looks || [])];
				const target = newLooks[lookIndex] || makeDefaultLook();
				newLooks[lookIndex] = {
					...target,
					colors: padColors(payload.colors),
				};
				return { ...prev, ...namePatch, looks: newLooks };
			});
			return;
		}
		if (payload?.name) {
			setValues((prev) => ({ ...prev, name: payload.name }));
		}
	};

	const addLook = (mode = 'copy') => {
		const currentLooks = values.looks || [];
		const baseColors = currentLooks.length > 0
			? padColors(currentLooks[currentLooks.length - 1].colors)
			: padColors(makeDefaultLook().colors);

		let colors;
		if (mode === 'rotate') {
			colors = rotateColors(baseColors, 1);
		} else if (mode === 'shuffle') {
			colors = shuffleColorsToZones(baseColors);
		} else {
			colors = baseColors.map((c) => ({ ...c }));
		}

		const newIndex = currentLooks.length;
		setValues({
			...values,
			type: 'cycle',
			looks: [...currentLooks, { colors, hold: MIN_CYCLE_HOLD_SECONDS }],
		});
		setExpandedLooks((prev) => ({ ...prev, [newIndex]: true }));
		setExpertLookIndex(newIndex);
	};

	const removeLook = (index) => {
		if ((values.looks || []).length <= 1) return;
		setValues({ ...values, looks: values.looks.filter((_, i) => i !== index) });
		setExpandedLooks((prev) => {
			const next = {};
			Object.keys(prev).forEach((key) => {
				const i = Number(key);
				if (i < index) next[i] = prev[i];
				else if (i > index) next[i - 1] = prev[i];
			});
			return next;
		});
	};

	const moveLook = (index, direction) => {
		const currentLooks = [...(values.looks || [])];
		const newIndex = index + direction;
		if (newIndex < 0 || newIndex >= currentLooks.length) return;
		[currentLooks[index], currentLooks[newIndex]] = [currentLooks[newIndex], currentLooks[index]];
		setValues({ ...values, looks: currentLooks });
		setExpandedLooks((prev) => {
			const next = { ...prev };
			const tmp = next[index];
			next[index] = next[newIndex];
			next[newIndex] = tmp;
			return next;
		});
	};

	const adjustLookHold = (index, deltaSteps) => {
		const currentLooks = [...(values.looks || [])];
		const current = normalizeCycleHold(currentLooks[index]?.hold);
		const next = Math.max(MIN_CYCLE_HOLD_SECONDS, current + deltaSteps * CYCLE_HOLD_STEP_SECONDS);
		currentLooks[index] = { ...currentLooks[index], hold: next };
		setValues({ ...values, looks: currentLooks });
	};

	const addColorToLook = (lookIndex) => {
		const currentLooks = [...(values.looks || [])];
		const look = { ...currentLooks[lookIndex] };
		look.colors = [...(look.colors || []), { r: 128, g: 128, b: 128 }];
		currentLooks[lookIndex] = look;
		setValues({ ...values, looks: currentLooks });
	};

	const save = async () => {
		const looks = (values.looks || []).map((look) => ({
			colors: look.colors || [],
			hold: values.type === 'single' ? 0 : normalizeCycleHold(look.hold),
		}));
		const type = values.type === 'cycle' ? 'cycle' : 'single';

		const data = {
			name: values.name || 'Untitled Scene',
			type,
			looks,
			system_id: currentSystem?.uid || scene?.system_id,
		};

		if (scene?.uid) {
			await updateDoc(doc(firestore, 'scenes', scene.uid), data);
		} else {
			await addDoc(collection(firestore, 'scenes'), {
				...data,
				createdAt: serverTimestamp(),
			});
		}
		doneEdit();
	};

	if (!open) return null;

	const currentLooks = values.looks || [];
	const isCycle = values.type === 'cycle';

	const renderLookControls = (lidx) => (
		<div className="flex items-center gap-1 text-xs flex-shrink-0" onClick={(e) => e.stopPropagation()}>
			{isCycle && (
				<div className="flex items-center gap-1 mr-1">
					<button
						type="button"
						onClick={() => adjustLookHold(lidx, -1)}
						disabled={normalizeCycleHold(currentLooks[lidx]?.hold) <= MIN_CYCLE_HOLD_SECONDS}
						className="px-1.5 py-0.5 border rounded disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
						title="−30 sec"
					>−</button>
					<span className="tabular-nums w-10 text-center text-gray-600 dark:text-gray-300">
						{normalizeCycleHold(currentLooks[lidx]?.hold)}s
					</span>
					<button
						type="button"
						onClick={() => adjustLookHold(lidx, 1)}
						className="px-1.5 py-0.5 border rounded hover:bg-gray-100 dark:hover:bg-gray-700"
						title="+30 sec"
					>+</button>
				</div>
			)}
			{isCycle && (
				<>
					<button
						type="button"
						onClick={() => moveLook(lidx, -1)}
						disabled={lidx === 0}
						className="px-1.5 py-0.5 border rounded disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
					>↑</button>
					<button
						type="button"
						onClick={() => moveLook(lidx, 1)}
						disabled={lidx === currentLooks.length - 1}
						className="px-1.5 py-0.5 border rounded disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
					>↓</button>
					<button
						type="button"
						onClick={() => removeLook(lidx)}
						disabled={currentLooks.length <= 1}
						className="px-1.5 py-0.5 border rounded text-red-600 disabled:opacity-40 hover:bg-red-50 dark:hover:bg-red-950"
					>×</button>
				</>
			)}
		</div>
	);

	const renderZoneEditors = (look, lidx) => {
		const colors = look.colors || [];

		return (
			<>
				<div className="flex items-center justify-between mb-1.5">
					<div className="text-xs text-gray-500 dark:text-gray-400">Zones ({colors.length})</div>
					<button
						type="button"
						onClick={() => addColorToLook(lidx)}
						className="text-[10px] px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
					>
						+ Zone
					</button>
				</div>
				<div className="flex flex-wrap gap-1.5">
					{colors.map((c, cidx) => (
						<div
							key={cidx}
							className="flex items-center gap-1 rounded-lg border px-1 py-0.5 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700"
						>
							<SceneColorPicker
								color={c}
								onChange={(rgb) => setLookColor(lidx, cidx, rgb)}
								label={colorNames[cidx] || defaultZoneName(cidx)}
								size={24}
								sceneColors={scenePalette}
								zIndex={70}
							/>
							<span className="text-[10px] text-gray-500 dark:text-gray-400 truncate max-w-[3.5rem]" title={colorNames[cidx] || defaultZoneName(cidx)}>
								{colorNames[cidx] || defaultZoneName(cidx)}
							</span>
						</div>
					))}
				</div>
			</>
		);
	};

	return (
		<div className="fixed inset-0 flex items-end sm:items-center justify-center bg-black/40 z-50 p-0 sm:p-4">
			<div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-auto">
				<div className="p-4 sm:p-6">
					<h3 className="font-semibold text-lg mb-4">Scene Editor</h3>

					<div className="mb-4">
						<label className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-1.5">Scene Name</label>
						<input
							className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2 w-full text-sm"
							value={values.name || ''}
							onChange={(e) => setValues({ ...values, name: e.target.value })}
							placeholder="Scene name"
						/>
					</div>

					<div className="mb-4">
						<label className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-1.5">Type</label>
						<select
							className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2 w-full text-sm"
							value={values.type}
							onChange={(e) => setType(e.target.value)}
						>
							<option value="single">Single (static colors)</option>
							<option value="cycle">Cycle (animated looks)</option>
						</select>
					</div>

					<div className="mb-4">
						{julesEnabled ? (
							<>
								<label className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-1.5">
									Color helper
								</label>
								<div className="flex gap-2 mb-3">
									<button
										type="button"
										onClick={() => setColorHelper('jules')}
										className={`flex-1 px-3 py-2 text-sm rounded-xl border transition-colors inline-flex items-center justify-center gap-2 ${
											colorHelper === 'jules'
												? 'bg-[#880e4f] text-white border-[#880e4f]'
												: 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
										}`}
									>
										<JulesIcon
											className={`h-4 w-4 ${colorHelper === 'jules' ? 'brightness-0 invert' : ''}`}
										/>
										Jules
									</button>
									<button
										type="button"
										onClick={() => setColorHelper('expert')}
										className={`flex-1 px-3 py-2 text-sm rounded-xl border transition-colors ${
											colorHelper === 'expert'
												? 'bg-[#880e4f] text-white border-[#880e4f]'
												: 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
										}`}
									>
										Expert
									</button>
								</div>
								{colorHelper === 'jules' ? (
									<AskJulesPanel
										enabled={open && julesEnabled}
										system={currentSystem}
										numZones={numColors || 4}
										onApply={applyJulesResult}
									/>
								) : (
									<ExpertColorPanel
										numColors={numColors || 4}
										targetLookIndex={expertLookIndex}
										lookCount={currentLooks.length}
										isCycle={isCycle}
										onApplyLook={applyExpertLook}
										onApplyNewLook={applyExpertNewLook}
										onApplyCycle={applyExpertCycle}
										onApplyAllLooks={applyExpertAllLooks}
									/>
								)}
							</>
						) : (
							<ExpertColorPanel
								numColors={numColors || 4}
								targetLookIndex={expertLookIndex}
								lookCount={currentLooks.length}
								isCycle={isCycle}
								onApplyLook={applyExpertLook}
								onApplyNewLook={applyExpertNewLook}
								onApplyCycle={applyExpertCycle}
								onApplyAllLooks={applyExpertAllLooks}
							/>
						)}
					</div>

					<div className="mb-2">
						<div className="flex items-center justify-between mb-2 gap-2">
							<label className="text-sm font-medium text-gray-600 dark:text-gray-400">
								{isCycle ? `Looks (${currentLooks.length}) — tap to expand` : 'Zones'}
							</label>
							<div className="flex flex-wrap items-center justify-end gap-1">
								{currentLooks.length > 0 && (
									<>
										<button
											type="button"
											onClick={() => addLook('rotate')}
											className="text-[10px] px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-800 inline-flex items-center gap-1"
											title="New look: rotate previous colors by one zone"
										>
											<RotateCcw size={12} /> Rotate
										</button>
										<button
											type="button"
											onClick={() => addLook('shuffle')}
											className="text-[10px] px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-800 inline-flex items-center gap-1"
											title="New look: randomly reassign colors to zones"
										>
											<Shuffle size={12} /> Shuffle
										</button>
									</>
								)}
								{currentLooks.length > 0 && (
									<button
										type="button"
										onClick={() => addLook('copy')}
										className="text-xs px-3 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
									>
										+ Add Look
									</button>
								)}
							</div>
						</div>
						{!isCycle && currentLooks.length > 0 && (
							<p className="text-[10px] text-gray-500 dark:text-gray-400 mb-2">
								Switch to Cycle type, or use Add Look to start a cycle from these colors.
							</p>
						)}

						<div className="space-y-2 max-h-[420px] overflow-auto pr-1">
							{currentLooks.map((look, lidx) => {
								const colors = look.colors || [];
								const expanded = !isCycle || expandedLooks[lidx];

								if (!isCycle) {
									return (
										<div key={lidx} className="border border-gray-200 dark:border-gray-700 rounded-xl p-2.5 bg-gray-50 dark:bg-gray-800">
											{renderZoneEditors(look, lidx)}
										</div>
									);
								}

								return (
									<div
										key={lidx}
										className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-gray-50 dark:bg-gray-800"
										onFocus={() => setExpertLookIndex(lidx)}
									>
										<button
											type="button"
											onClick={() => toggleLookExpanded(lidx)}
											className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
										>
											{expanded ? (
												<ChevronDown size={16} className="text-gray-500 flex-shrink-0" />
											) : (
												<ChevronRight size={16} className="text-gray-500 flex-shrink-0" />
											)}
											<span className="text-sm font-medium flex-shrink-0">Look {lidx + 1}</span>
											<SVGColorBlock size={28} data={colors} />
											<span className="text-xs text-gray-500 dark:text-gray-400 flex-1 truncate">
												{normalizeCycleHold(look.hold)}s hold
											</span>
											{renderLookControls(lidx)}
										</button>
										{expanded && (
											<div className="px-2.5 pb-2.5 pt-1 border-t border-gray-200 dark:border-gray-700">
												{renderZoneEditors(look, lidx)}
											</div>
										)}
									</div>
								);
							})}
						</div>
					</div>
				</div>

				<div className="px-6 py-4 border-t dark:border-gray-700 flex justify-end gap-3 bg-gray-50 dark:bg-gray-800 rounded-b-2xl sticky bottom-0">
					<button onClick={doneEdit} className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">Cancel</button>
					<button onClick={save} className="px-4 py-2 text-sm bg-[#880e4f] text-white rounded-lg hover:bg-[#6b0b3f]">Save Scene</button>
				</div>
			</div>
		</div>
	);
}

export default SceneEditor;
